import { useRef, useState } from 'react';
import { Link } from '@tanstack/react-router';
import useReveal from '../../hooks/useReveal.js';
import useInView from '../../hooks/useInView.js';
import useReducedMotion from '../../hooks/useReducedMotion.js';
import Eyebrow from '../ui/Eyebrow.jsx';
import Check from '../ui/Check.jsx';
import Button from '../ui/Button.jsx';
import { offersHead, offers, auditBand } from '../../content/offers.js';

/** Dwell time per tab before the auto-rotate advances (ms). Fed to CSS as --ov2-dwell. */
const DWELL_MS = 5000;

/**
 * Offers — OffersV2 "Vertical Tab / Spotlight" layout.
 * Free-audit front door (reuses the .ov2-panel glass card) sits above.
 * Left nav column: three tabs, each activates on hover, focus, or click.
 * Right panel: detail view of the active offer, cross-fades on tab change.
 */
export default function Offers() {
  const [i, setI] = useState(0);
  const [locked, setLocked] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [focusWithin, setFocusWithin] = useState(false);
  const current = offers.flagship[i];

  const reduced = useReducedMotion();
  const [navRef, inView] = useInView();
  const headRef    = useReveal();
  const shellRef   = useReveal();

  // Single clock: the active tab's progress bar (CSS) drives the advance via
  // onAnimationEnd, so the bar and the timer can never drift. Rotation runs only
  // once the tabs are in view, motion is allowed, and the visitor hasn't locked a choice.
  const lockedRef = useRef(false);
  const paused = hovered || focusWithin;
  const rotating = inView && !reduced && !locked;

  const advance = (e) => {
    if (e.animationName !== 'ov2-progress' || lockedRef.current) return;
    setI((p) => (p + 1) % offers.flagship.length);
  };

  const lockTo = (idx) => {
    lockedRef.current = true; // first real pick stops auto-rotation for the session
    setLocked(true);
    setI(idx);
  };

  return (
    <section id="offers" className="ov2-root">
      <div className="container-page">

        {/* Head row: eyebrow + h2 left, description right */}
        <div ref={headRef} className="ov2-head">
          <div>
            <Eyebrow>{offersHead.eyebrow}</Eyebrow>
            <h2 className="ov2-heading">{offersHead.heading}</h2>
          </div>
          <p className="ov2-desc">{offersHead.description}</p>
        </div>

        {/* Free-audit front door — reuses the existing glass panel treatment */}
        <div className="ov2-panel mb-7">
          <Eyebrow>{auditBand.eyebrow}</Eyebrow>
          <h3 className="ov2-panel-title">{auditBand.heading}</h3>
          <p className="ov2-panel-desc">{auditBand.body}</p>
          <div className="mt-6">
            <Button variant="brand" as={Link} to="/intake">
              {auditBand.cta.label}
            </Button>
          </div>
        </div>

        {/* Main shell: nav left, panel right */}
        <div
          ref={shellRef}
          className="ov2-shell"
          style={{ '--ov2-dwell': `${DWELL_MS}ms` }}
          data-ov2-paused={paused ? 'true' : 'false'}
          onPointerEnter={() => setHovered(true)}
          onPointerLeave={() => setHovered(false)}
          onFocus={() => setFocusWithin(true)}
          onBlur={(e) => {
            if (!e.currentTarget.contains(e.relatedTarget)) setFocusWithin(false);
          }}
        >

          {/* Nav column — --i CSS var drives the sliding rail */}
          <nav ref={navRef} className="ov2-nav" style={{ '--i': i }}>
            <span className="ov2-rail" aria-hidden="true" />
            {offers.flagship.map((offer, idx) => (
              <button
                key={offer.id}
                className={`ov2-tab${idx === i ? ' is-active' : ''}`}
                onMouseEnter={() => setI(idx)}
                onFocus={() => setI(idx)}
                onClick={() => lockTo(idx)}
                aria-current={idx === i ? 'true' : undefined}
              >
                <span className="ov2-tab-n">0{idx + 1}</span>
                <span className="ov2-tab-t">{offer.title}</span>
                <span className="ov2-tab-s">{offer.subtitle}</span>
                {rotating && idx === i && (
                  <span
                    className="ov2-tab-progress"
                    aria-hidden="true"
                    onAnimationEnd={advance}
                  />
                )}
              </button>
            ))}
          </nav>

          {/* Panel — key={current.id} remounts on tab change so ov2-fade reruns */}
          <div className="ov2-panel" key={current.id}>
            <div className="ov2-panel-head">
              <span className="ov2-n-big">0{i + 1}</span>
              <div>
                <Eyebrow>{current.subtitle}</Eyebrow>
                <h3 className="ov2-panel-title">{current.title}</h3>
              </div>
            </div>

            <p className="ov2-panel-desc">{current.description}</p>

            <ul className="ov2-panel-features">
              {current.features.map((f, fi) => (
                <li key={f} style={{ animationDelay: `${fi * 70}ms` }}>
                  <Check />
                  <span>{f}</span>
                </li>
              ))}
            </ul>

            <div className="ov2-panel-foot">
              <div className="ov2-meta">
                <span><em>Timeline</em>{current.meta.timeline}</span>
                <span><em>Model</em>{current.meta.model}</span>
              </div>
              <Button variant="brand" as={Link} to="/intake">
                {current.cta.label}
              </Button>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
}
